import { CheckCircle2, Circle, Loader2, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  createManagerLaunchChecklistState,
  type ManagerLaunchTask,
} from "@/lib/manager-launch-checklist";
import { cn } from "@/lib/utils";
import type { Site } from "@/types";
import type { ManagerReportResponse } from "@/types/reports";

const DISMISS_KEY = "stonecut:manager-launch-checklist-dismissed";

type ManagerLaunchChecklistProps = {
  sites: Site[] | undefined;
  report: ManagerReportResponse | undefined | null;
  isLoading: boolean;
};

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function writeDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, "1");
  } catch {
    // ignore storage failures
  }
}

export function ManagerLaunchChecklist({
  sites,
  report,
  isLoading,
}: ManagerLaunchChecklistProps) {
  const [dismissed, setDismissed] = useState(() => readDismissed());

  function handleDismiss() {
    writeDismissed();
    setDismissed(true);
  }

  if (dismissed) {
    return null;
  }

  if (isLoading && !sites && !report) {
    return (
      <Card className="border-dashed bg-card/94">
        <CardHeader className="flex flex-row items-center gap-3">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
          <CardTitle className="text-base">Checking launch setup...</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  const checklist = createManagerLaunchChecklistState({ sites, report });

  if (!checklist.shouldShow) {
    return null;
  }

  return (
    <Card className="overflow-hidden bg-card/96">
      <CardHeader className="gap-4 md:grid-cols-[minmax(0,1fr)_auto]">
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
            Launch setup
          </p>
          <CardTitle className="text-2xl tracking-tight">
            Get this account ready for live crews
          </CardTitle>
          <CardDescription className="max-w-2xl text-sm leading-6">
            Finish these steps before sending workers to site. The analytics
            dashboard takes over after first sign-in.
          </CardDescription>
        </div>
        <CardAction className="flex flex-col items-end gap-3">
          <div className="flex items-center gap-1">
            <span className="text-sm font-semibold text-foreground">
              {checklist.progressLabel}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label="Dismiss launch setup"
              onClick={handleDismiss}
            >
              <X className="size-4" />
            </Button>
          </div>
        </CardAction>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {checklist.tasks.map((task) => (
            <ChecklistTaskCard key={task.id} task={task} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ChecklistTaskCard({ task }: { task: ManagerLaunchTask }) {
  const Icon = task.complete ? CheckCircle2 : Circle;
  const content = (
    <div
      className={cn(
        "flex h-full flex-col gap-4 rounded-md border border-border/60 bg-background/70 p-4",
        task.complete && "border-success/35 bg-success/8",
        task.disabled && "opacity-60"
      )}
    >
      <div className="flex items-start gap-3">
        <Icon
          className={cn(
            "mt-0.5 size-5 shrink-0 text-muted-foreground",
            task.complete && "text-success"
          )}
          aria-hidden
        />
        <div className="min-w-0 space-y-1">
          <p className="font-semibold text-foreground">{task.title}</p>
          <p className="text-sm leading-5 text-muted-foreground">
            {task.description}
          </p>
        </div>
      </div>
      <div className="mt-auto">
        {task.disabled ? (
          <Button
            type="button"
            variant={task.complete ? "outline" : "light"}
            size="sm"
            className="w-full"
            disabled
          >
            {task.ctaLabel}
          </Button>
        ) : (
          <Button
            asChild
            variant={task.complete ? "outline" : "light"}
            size="sm"
            className="w-full"
          >
            <Link to={task.href}>{task.ctaLabel}</Link>
          </Button>
        )}
      </div>
    </div>
  );

  return content;
}
